import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Photo from "./Photo";

/**
 * Reusable internal-linking rail.
 *
 *   <InternalRail
 *     eyebrow="Related reading · The Journal"
 *     headline="Keep reading"
 *     italic="on the topic."
 *     theme="ink"          // "ink" or "bone"
 *     items={[
 *       { to: "/blog/foo", label: "Title", sublabel: "5 min · Photography intent", image: "hero-spread-01", testId: "rail-foo" },
 *     ]}
 *     cols={3}              // 2, 3, or 4
 *   />
 */
export default function InternalRail({
  eyebrow,
  headline,
  italic,
  theme = "ink",
  items = [],
  cols = 3,
}) {
  if (!items.length) return null;
  const isInk = theme === "ink";
  const colClass =
    cols === 4 ? "md:grid-cols-2 lg:grid-cols-4"
      : cols === 2 ? "md:grid-cols-2"
        : "md:grid-cols-3";

  return (
    <section
      className={`${isInk ? "bg-ink text-bone border-t border-bone/10" : "bg-bone text-ink border-t border-ink/10"} py-20 lg:py-28`}
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            {eyebrow && (
              <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">
                {eyebrow}
              </p>
            )}
            <h2
              className="font-display tracking-editorial leading-[0.95]"
              style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}
            >
              {headline}
              {italic && (
                <>
                  {" "}
                  <span className="italic font-light text-ember">{italic}</span>
                </>
              )}
            </h2>
          </div>
        </div>

        <div
          className={`grid grid-cols-1 ${colClass} gap-px ${isInk ? "bg-bone/10 border border-bone/10" : "bg-ink/10 border border-ink/10"}`}
        >
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.testId || `rail-${it.to.replace(/\W+/g, "-")}`}
              className={`group ${isInk ? "bg-ink hover:bg-[#161613]" : "bg-bone hover:bg-ink hover:text-bone"} transition-colors p-6 md:p-7 flex flex-col`}
            >
              {it.image && (
                <Photo
                  slug={it.image}
                  alt={it.label}
                  ratio="4/3"
                  sizes="(min-width: 1024px) 30vw, 100vw"
                />
              )}
              {it.sublabel && (
                <p
                  className={`mt-5 text-[10px] uppercase tracking-wider2 font-bold ${isInk ? "text-ember" : "text-ember"}`}
                >
                  {it.sublabel}
                </p>
              )}
              <h3
                className={`${it.image ? "mt-3" : ""} font-display text-lg md:text-xl tracking-editorial leading-tight ${isInk ? "group-hover:text-ember" : "group-hover:text-ember"} transition-colors`}
              >
                {it.label}
              </h3>
              {it.blurb && (
                <p
                  className={`mt-3 text-sm leading-relaxed ${isInk ? "text-bone/65 group-hover:text-bone/85" : "text-char-mute group-hover:text-bone/70"} transition-colors line-clamp-3`}
                >
                  {it.blurb}
                </p>
              )}
              <div
                className={`mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-wider2 font-bold ${isInk ? "text-ember" : "text-ember"}`}
              >
                {it.cta || "Read more"}{" "}
                <ArrowUpRight
                  size={12}
                  strokeWidth={2}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
